import React from 'react';
import { bffQuery } from '../../../lib/bffClient';
import {
  jsToIrDow, doctorAbsentOn, doctorHasNoShift, presenceSlots, freeSlots,
  calendarDow, clinicDayWindow, dayIsoOf, isClinicToday, slotInstant,
} from '../helpers';
import { holdsSlot } from '../../../constants/appointmentStatus';

// The slots the manual time grid may offer for one doctor on one day, with every slot the
// desk cannot actually book already removed: outside the doctor's presence, or already
// taken. This is the single source of truth behind both booking modals — the old grid
// showed the full 08:00–20:00 range and let the server reject the collisions, which trained
// staff to distrust it.
//
// Two exclusions, in order:
//   1. presence — a slot that does not START inside the doctor's shift is dropped (no
//                 "show all" fallback, and a doctor with NO registered shift is present
//                 for nothing). The last visit may run past the end of the shift.
//   2. booked   — a slot overlapping an appointment that still HOLDS its hour is dropped.
//
// Neither exclusion has an override. «خارج از نوبت» does not widen this grid — it
// replaces it: a booking with no hour asks nothing of the slots, so the caller hides
// the grid rather than asking it for more.
//
// `shiftless` — a performer who is not held to a shift at all: a STAFF member
// performing a service session. Presence is a DOCTOR's contract (the scheduling
// service checks `doctor_presences` only for provider_type 'doctor'); a staff
// performer's availability is their diary, so exclusion 1 does not apply and the
// grid is `timeSlots` (which the caller bounds to opening hours) minus bookings.
//
// Bookings are matched on the PERFORMER — `providerId`, falling back to `doctorId`
// for rows written before providers existed — because that is what the service
// keys its overlap check on: a doctor's own service session blocks his visit
// hour, and a physiotherapist's session blocks hers.
//
// A third exclusion, on today only: an hour that has already passed. The grid
// used to offer 09:00 at 11:40 and let the desk book a visit into the past.
//
// The booked list is fetched per day. A fetch failure is soft: we fall back to the
// presence-filtered slots and let the server's own double-booking guard be the backstop,
// because blocking all booking because one list did not load is the worse failure.

export const SCHEDULED_FOR_DAY = `
  query ScheduledForDay($startTime: String, $endTime: String) {
    scheduledAppointments(startTime: $startTime, endTime: $endTime) {
      id doctorId providerId dateTime status requestExpiresAt
    }
  }
`;

// The clinic's midnight-to-midnight of the day `base` stands for, as epoch-ms strings.
// The grid builds each slot on the CLINIC's clock (`slotInstant`, PM-0088), so the
// query window is the clinic's day too, or a day's own bookings could fall outside it.
export function localDayRange(base) {
  return clinicDayWindow(base);
}

/**
 * The day's bookings that belong to THIS provider and still hold their hour, from
 * one day's `scheduledAppointments` — the same reading the grid and the
 * «اولین نوبت» search both make.
 */
export function providerBookings(appointments, providerId, slotMin, excludeAppointmentId = null) {
  // `holdsSlot`, not `status !== 'cancelled'`: a REJECTED request releases its
  // hour exactly as a cancellation does, and the desk that just answered «رد»
  // is usually the one about to give that hour to somebody else. Filtering on
  // `cancelled` alone left a refused request hiding a slot the scheduling
  // service would happily have booked.
  return (appointments || [])
    .filter((a) => (a.providerId || a.doctorId) === providerId && holdsSlot(a))
    // «ویرایش نوبت»: the appointment being moved holds the hour it is leaving, and
    // must not hide that hour — or the ones its visit overlaps — from its own grid.
    // The service makes the same exception (`exclude_appointment_id`).
    .filter((a) => !excludeAppointmentId || a.id !== excludeAppointmentId)
    .map((a) => ({ startMs: Number(a.dateTime), durMin: slotMin }));
}

/**
 * The slots the desk may book on `selectedDate`, from the grid, the shift and the
 * day's bookings — the three exclusions above as one pure step, so the manual
 * grid and the «اولین نوبت» search cannot disagree about what is free.
 */
export function selectableSlots({ timeSlots, presences, shiftless = false, selectedDate, slotMin, booked, nowMs = Date.now() }) {
  const irDow = jsToIrDow(calendarDow(selectedDate));
  const absent = !shiftless && doctorAbsentOn(presences, irDow);
  if (absent) return [];
  const inPresence = shiftless ? timeSlots : presenceSlots(timeSlots, presences, irDow);
  const free = freeSlots(inPresence, selectedDate, slotMin, booked);
  if (!isClinicToday(selectedDate, nowMs)) return free;
  return free.filter((s) => slotInstant(selectedDate, s.h, s.m) >= nowMs);
}

export function useSelectableSlots({ doctorId, presences, timeSlots, selectedDate, slotMin, shiftless = false, excludeAppointmentId = null }) {
  const [booked, setBooked] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  // Re-fetch when the doctor or the day changes. dateKey collapses the Date to the day so a
  // new Date object for the same day does not re-trigger.
  const dayKey = dayIsoOf(selectedDate);

  React.useEffect(() => {
    if (!doctorId) { setBooked([]); return; }
    let cancelled = false;
    setLoading(true);
    const { startTime, endTime } = localDayRange(selectedDate);
    bffQuery(SCHEDULED_FOR_DAY, { startTime, endTime })
      .then(data => {
        if (cancelled) return;
        setBooked(providerBookings(data?.scheduledAppointments, doctorId, slotMin, excludeAppointmentId));
      })
      .catch(() => { if (!cancelled) setBooked([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [doctorId, dayKey, slotMin, excludeAppointmentId]);

  const irDow = jsToIrDow(calendarDow(selectedDate));
  const noShift = !shiftless && doctorHasNoShift(presences);
  const absent = !shiftless && doctorAbsentOn(presences, irDow);

  // The minute, not the millisecond, so a re-render does not recompute the grid
  // for nothing; and only on today, where the past exists.
  const isToday = isClinicToday(selectedDate);
  const nowMinute = isToday ? Math.floor(Date.now() / 60000) : 0;

  const slots = React.useMemo(
    () => selectableSlots({ timeSlots, presences, shiftless, selectedDate, slotMin, booked, nowMs: isToday ? nowMinute * 60000 : 0 }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shiftless, timeSlots, presences, irDow, dayKey, slotMin, booked, isToday, nowMinute],
  );

  return {
    slots,
    loading,
    // Reported separately because they are fixed in different places: an empty schedule
    // is corrected once in «پرسنل», a day off by picking another day. `absent` stays true
    // for both so callers that only care "can we book today" keep working.
    noShift,                                 // no shift registered at all
    absent,                                  // doctor does not work this day
    allBooked: !absent && slots.length === 0, // works, but every slot is taken
  };
}
